import { Redirect, router } from "expo-router";
import { useState } from "react";
import { View } from "react-native";

import Button from "$src/core/components/atoms/Button/Button";
import Image from "$src/core/components/atoms/Image/Image";
import ScrollView from "$src/core/components/atoms/ScrollView/ScrollView";
import Text from "$src/core/components/atoms/Text/Text";
import TextInput from "$src/core/components/atoms/TextInput/TextInput";
import useAuth from "$src/core/hooks/use-auth";
import useConfig from "$src/core/hooks/use-config";
import useTranslation from "$src/core/hooks/use-translation";
import LoadingPage from "$src/core/pages/LoadingPage/LoadingPage";
import Alerter from "$src/core/services/alerting";
import Logging from "$src/core/services/logging/logging";

import $ from "./PairPage.styles";

type Props = {
  code?: string;
};

const PairPage: React.FC<Props> = ({ code }) => {
  const { t } = useTranslation();
  const { logo } = useConfig();
  const { status: authStatus, authorizePairing } = useAuth();

  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [pairingCode, setPairingCode] = useState(code ?? "");

  const handlePair = async () => {
    try {
      setStatus("loading");

      const success = await authorizePairing(pairingCode);

      if (success) {
        setStatus("success");
      } else {
        setStatus("error");
        Alerter.error(t("ERROR/AUTH/PAIR"));
      }
    } catch (error) {
      Alerter.error(t("ERROR/AUTH/PAIR"));
      Logging.error("Failed to authorize pairing code", { error });
      setStatus("error");
    }
  };

  if (!authStatus) {
    return <LoadingPage />;
  }

  if (authStatus === "unauthenticated") {
    return <Redirect href="/login?redirect=/pair" />;
  }

  return (
    <ScrollView contentContainerStyle={$.container} accessibilityRole="list">
      <View style={$.form}>
        <View style={$.header}>
          <Image
            source={logo}
            style={$.logo}
            contentFit="contain"
            contentPosition="left"
            accessibilityRole="image"
            accessibilityLabel={t("COMMON/LOGO_ACCESSIBILITY_LABEL")}
          />

          <Text preset="heading1" accessibilityRole="text">
            {t("AUTH/PAIR/TITLE")}
          </Text>
          <Text preset="body" accessibilityRole="text">
            {t("AUTH/PAIR/SUBTITLE")}
          </Text>
        </View>

        <View style={$.field}>
          <Text preset="heading5" accessibilityRole="text">
            {t("AUTH/PAIRING_CODE/INPUT/LABEL")}
          </Text>
          <TextInput
            autoCapitalize="none"
            placeholder={t("AUTH/PAIRING_CODE/INPUT/PLACEHOLDER")}
            accessibilityLabel={t("AUTH/PAIRING_CODE/INPUT/LABEL")}
            accessibilityRole="text"
            onChangeText={(text) => {
              setPairingCode(text.toUpperCase());
              if (status === "error") {
                setStatus("idle");
              }
            }}
            editable={status !== "loading" && status !== "success"}
          />
        </View>
        <Button
          name="pair"
          preset="primary"
          onPress={handlePair}
          label={status === "success" ? t("AUTH/PAIRED") : t("AUTH/PAIR")}
          isLoading={status === "loading"}
          isDisabled={status === "success"}
          style={$.button}
        />
        {status === "success" && (
          <Button
            name="back_to_home"
            preset="primary"
            onPress={() => router.replace("/")}
            label={t("COMMON/BACK_TO_HOME")}
            isLoading={false}
            isDisabled={false}
            style={$.button}
          />
        )}
      </View>
    </ScrollView>
  );
};

export default PairPage;
